import React from 'react';
import useReveal from '../../hooks/useReveal';

const CloudServicesAgreement = () => {
  document.title = 'Cloud Services Agreement - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-xl font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>Cloud Services Agreement (CSA)</h1>

      <section className="mb-8">
        <p className="mb-4 font-bold">
          IMPORTANT: THESE TERMS AND CONDITIONS CONTAIN A DISPUTE RESOLUTION AND
          ARBITRATION PROVISION, INCLUDING A CLASS ACTION WAIVER. DEPENDING ON
          THE JURISDICTION, THAT SECTION MAY NOT APPLY.
        </p>
      </section>

      <section className="mb-8">
        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>
              Terms and Conditions, Related Agreement, Policies and Order Form.
            </strong>
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                This Cloud Services Agreement ("CSA") is an agreement entered
                into by Cloud Vantage ("Company") and the entity or person
                agreeing to these terms as set forth in the applicable order
                form ("Customer"), which order form is incorporated herein by
                this reference (together with any subsequent order forms
                submitted by Customer, collectively, the "Order"), and governs
                the leasing of the hardware, licenses, software and purchases
                the support services and other related products and cloud
                services (as applicable) ordered by Customer as set forth in the
                Order (collectively, the "Cloud Services").
              </li>
              <li>
                Company and Customer may be individually referred to herein as a
                "Party" and together the "Parties."
              </li>
              <li>
                This CSA is subject to the Order, Company's{' '}
                <a
                  href="/terms-of-use"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Universal Terms of Use
                </a>{' '}
                ("UToU"),{' '}
                <a
                  href="/acceptable-use-policy"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Acceptable Use Policy
                </a>{' '}
                ("AUP"),{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Privacy Policy
                </a>{' '}
                ("Privacy Policy"), and{' '}
                <a
                  href="/data-processing"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Data Processing Addendum
                </a>{' '}
                ("DPA"), all of which incorporated herein by reference. The
                UToU, AUP,{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Privacy Policy
                </a>{' '}
                and Order shall collectively be referred to as the "Agreements."
                In the event of a conflict between the documents that make up
                the Agreements, the documents will control in the following
                order (of decreasing precedence): the{' '}
                <a
                  href="/data-processing"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Data Processing Addendum
                </a>
                , the{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Privacy Policy
                </a>
                , AUP, this CSA and the remainder of the Agreements (excluding
                the foregoing).
              </li>
              <li>
                The person listed on the signature page of the Order
                ("Signatory") enters this CSA on behalf of Customer, and
                represents and warrants that the Signatory
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    has full legal authority to bind Customer to the Agreements;
                  </li>
                  <li>has read and understands the Agreements; and</li>
                  <li>agrees, on behalf of Customer, to the Agreements.</li>
                </ol>
              </li>
            </ol>
          </li>
          <li>
            <strong>Term and Termination.</strong>
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                <strong>Effective Date and Term.</strong> This CSA is effective
                on the date Company signs the Order (the "Effective Date"). The
                Order shall commence as of the Effective Date and shall remain
                in effect for the time period listed in the Order, unless
                earlier terminated as provided herein (the "Term").
              </li>
              <li>
                <strong>Termination for Breach.</strong> To the extent permitted
                by applicable law, either Party may terminate the CSA
                immediately on written notice if
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    the other party is in material breach of the Agreements and
                    fails to cure that breach within ten (10) business days
                    after receipt of written notice of the breach; or
                  </li>
                  <li>
                    the other party ceases its business operations or becomes
                    subject to insolvency proceedings and the proceedings are
                    not dismissed within ninety (90) days.
                  </li>
                </ol>
              </li>
              <li>
                <strong>Trial Period.</strong> If Customer enters into an Order,
                Company may provide Customer with a trial for the time period
                set forth in the applicable Order beginning on the Effective
                Date ("Trial Period"). Company will make the Cloud Services
                available to Customer on a trial basis until the end of the
                Trial Period. Within three (3) days of the end of the Trial
                Period, Customer shall notify Company of its desire to continue
                with or cancel the applicable Order. In the event Customer
                desires to cancel the applicable Order at the end of the Trial
                Period, Customer shall be responsible for the fees related to
                the Trial Period as specified in the applicable Order and the
                applicable Order shall be canceled. The Trial Period is limited
                to one Trial Period per account. Company, as determined in its
                sole discretion, reserves the right to revoke, restrict and/or
                terminate access to the Cloud Services during the Trial Period.
              </li>
              <li>
                <strong>
                  Termination Due to Applicable Law; Violation of Laws.
                </strong>{' '}
                Company may terminate the CSA immediately on written notice if
                Company reasonably believes that
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    continued provision or portion of any Cloud Service used by
                    Customer would violate applicable law(s); or
                  </li>
                  <li>
                    Customer has violated or caused Company to violate any
                    applicable commercial and public anti-bribery laws.
                  </li>
                </ol>
              </li>
              <li>
                <strong>Effects of Termination.</strong> If any of the
                Agreements are terminated, then
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    all rights and access to the Cloud Services will terminate
                    (including access to Customer Content, if applicable),
                    unless otherwise described in this CSA; and
                  </li>
                  <li>
                    all amounts owed by Customer to Company are immediately due
                    upon Customer's receipt of the final electronic bill or as
                    stated in the final invoice.
                  </li>
                </ol>
              </li>
            </ol>
          </li>
          <li>
            <strong>Provision of the Cloud Services.</strong>
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                <strong>Cloud Services Use.</strong> During the Term (as defined
                herein), Company will provide the Cloud Services in accordance
                with the Order and Agreements, including the{' '}
                <a
                  href="/sla"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  Service Level Agreement
                </a>{' '}
                ("SLA"), and Customer may use the Cloud Services, and integrate
                and use the Cloud Services in accordance with the Agreements.
              </li>
              <li>
                <strong>Customer Account and Admin Console.</strong> Customer
                must have a Customer account to use the Cloud Services
                ("Account") and is responsible for the information it provides
                to create the Account, the security of its passwords for the
                Account, and for any use of its Account. Company has no
                obligation to provide multiple accounts to Customer. Within the
                Account, Customer will have access to an Admin Console or
                similar application, through which Customer may manage use of
                the Cloud Services.
              </li>
              <li>
                <strong>Modifications.</strong>
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    <strong>To the Cloud Services.</strong> Company may make
                    commercially reasonable updates to the Cloud Services from
                    time to time. Company will inform Customer if Company makes
                    a material change to the Cloud Services that has a material
                    impact on Customer's use of the Cloud Services provided that
                    Customer has subscribed with Company to be informed about
                    such change.
                  </li>
                  <li>
                    <strong>To the Agreements.</strong> Company may make changes
                    to the Agreements and pricing from time to time. Unless
                    otherwise noted by Company, material changes to the
                    Agreements will become effective thirty (30) days after they
                    are posted, except to the extent the changes apply to new
                    functionality or the{' '}
                    <a
                      href="/data-processing"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 no-underline"
                    >
                      DPA
                    </a>
                    , or are required by applicable law, in which case they will
                    be effective immediately. Company will provide at least
                    thirty (30) days' advance notice for materially adverse
                    changes to any{' '}
                    <a
                      href="/sla"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 no-underline"
                    >
                      SLAs
                    </a>{' '}
                    by
                    <ol className="list-decimal pl-6 mt-2 mb-2">
                      <li>
                        sending an email to the email address located in the
                        Account; or
                      </li>
                      <li>
                        posting a notice to the applicable{' '}
                        <a
                          href="/sla"
                          target="_blank"
                          className="text-blue-600 hover:text-blue-800 no-underline"
                        >
                          SLAs
                        </a>{' '}
                        webpage.
                      </li>
                    </ol>
                    If Customer does not agree to the revised Agreements,
                    Customer may stop using the Cloud Services. Customer's
                    continued use of the Cloud Services after such material
                    change will constitute Customer's consent to such changes.
                    Company will post any modification to the Agreements.
                  </li>
                  <li>
                    <strong>
                      To the{' '}
                      <a
                        href="/data-processing"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 no-underline"
                      >
                        Data Processing Addendum
                      </a>
                      .
                    </strong>{' '}
                    Company may only change the DPA
                    <ol className="list-decimal pl-6 mt-2 mb-2">
                      <li>
                        where such change is required to comply with applicable
                        law, is expressly permitted by the{' '}
                        <a
                          href="/data-processing"
                          target="_blank"
                          className="text-blue-600 hover:text-blue-800 no-underline"
                        >
                          Data Processing Addendum
                        </a>
                        ; is commercially reasonable;
                      </li>
                      <li>
                        does not result in a material reduction of the security
                        of the Cloud Services;
                      </li>
                      <li>
                        does not expand the scope of or remove any restrictions
                        on Company's processing of Personal Data, as described
                        in the DPA; or
                      </li>
                      <li>
                        does not otherwise have a material adverse impact on
                        Customer's rights under the DPA.
                      </li>
                    </ol>
                    If Company makes a material change to the DPA in accordance
                    with this Section, Company will post the change at the
                    webpage containing the{' '}
                    <a
                      href="/data-processing"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 no-underline"
                    >
                      Data Processing Addendum
                    </a>
                    .
                  </li>
                </ol>
              </li>
              <li>
                <strong>Discontinuation of the Cloud Services.</strong> Company
                will notify Customer at least three (3) months before
                discontinuing any portion of the Cloud Service (or associated
                material functionality) unless Company replaces such
                discontinued portion or functionality with a materially similar
                portion or functionality. Nothing in this Section limits
                Company's ability to make changes required to comply with
                applicable law, address a material security or data privacy
                risk, or avoid a substantial economic or material technical
                burden. This Section does not apply to pre-general availability
                of the Cloud Services, offerings, or functionality.
              </li>
            </ol>
          </li>
          <li>
            <strong>Payment Terms.</strong>
            <ol className="list-decimal pl-6 mt-2 mb-2">
              <li>
                The Order is subject to the payment terms stated therein.
                Customer agrees to pay the fees specified in the applicable
                Order. Fees are billed as provided in an Order.
              </li>
              <li>
                Subject to any legal requirements, fees due under this CSA may
                include any local, state, federal or foreign taxes, levies or
                duties of any nature, including, value-added, sales use or
                withholding taxes (collectively, "Taxes"). If Company has a
                legal obligation to pay or collect Taxes for which Customer is
                responsible, the appropriate amount shall be invoiced to and
                paid by Customer unless Customer provides Company with a valid
                tax exemption certificate authorized by the appropriate taxing
                authority.
              </li>
              <li>
                <strong>Taxes.</strong>
                <ol className="list-decimal pl-6 mt-2 mb-2">
                  <li>
                    Customer is responsible for all government-imposed taxes
                    (including, without limitation, any local, state, federal or
                    foreign taxes, levies or duties of any nature, including,
                    value-added, sales use or withholding taxes), except for
                    taxes based on Company's net income, net worth, asset value,
                    property value, or employment ("Taxes"). Customer will pay
                    Company for the Cloud Services without any reduction for
                    Taxes. If Company is obligated to collect or pay any Taxes,
                    the Taxes will be invoiced to Customer and Customer will pay
                    such Taxes to Company, unless Customer provides Company with
                    a timely and valid tax exemption certificate in respect of
                    those Taxes.
                  </li>
                  <li>
                    Customer will provide Company with any applicable tax
                    identification information that Company may require under
                    applicable law to ensure its compliance with applicable tax
                    regulations and authorities in applicable jurisdictions.
                    Customer will be liable to pay (or reimburse Company for)
                    any Taxes, interest, penalties, or fines arising out of any
                    improperly or false declaration by Customer.
                  </li>
                </ol>
              </li>
              <li>
                <strong>Payment Disputes and Refunds.</strong> Any payment
                disputes must be submitted in writing before the payment due
                date. Refunds (if any) are at Company's discretion and will only
                be in the form of credit for the Cloud Services. Nothing in the
                Agreements obligates Company to extend credit to any party.
              </li>
              <li>
                <strong>Delinquent Payments; Suspension.</strong> Customer will
                be responsible for all reasonable expenses (including attorneys'
                fees) incurred by Company in collecting delinquent amounts.
                Further, if Customer's payment for the Cloud Services is
                overdue, Company may disable or limit access to or use of the
                Cloud Services or components of the Cloud Services. The Company
                shall decide the time limit within which it can exercise its
                power to disable or limit access to Customer.
              </li>
            </ol>
            <li>
              <strong>Customer Responsibilities.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Customer Content.</strong> Customer is solely
                  responsible for the quality, performance and all other aspects
                  of any data provided to Company by Customer or the individuals
                  or entities (include employees of Customer or Customer's
                  affiliates and other authorized third parties) who use or
                  visit Customer's websites and applications or are permitted by
                  Customer to use the Cloud Services (collectively, "End-Users")
                  through the Cloud Services (collectively, "Customer Content").
                  Customer shall be fully and solely responsible for uploading,
                  supplementing, modifying and updating the Customer Content.
                </li>
                <li>
                  <strong>Back-Ups.</strong> Customer is solely responsible for
                  making back-up copies of Customer Content.
                </li>
                <li>
                  <strong>Customer Personnel.</strong> Customer shall be
                  responsible for and allocate engineering personnel and
                  resources to enable Company to deliver and perform the Cloud
                  Services.
                </li>
                <li>
                  <strong>Compliance.</strong> Customer will
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      ensure that Customer End-Users comply with the Agreements;
                    </li>
                    <li>
                      use commercially reasonable efforts to prevent and
                      terminate any unauthorized use of, or access to, the Cloud
                      Services; and
                    </li>
                    <li>
                      promptly notify Company of any unauthorized use of, or
                      access to, the Cloud Services, Account, or Customer's
                      password of which Customer becomes aware.
                    </li>
                  </ol>
                  <p class="mt-2">
                    Company reserves the right to investigate any potential
                    violation of the AUP by Customer, which may include
                    reviewing Customer content or data.
                  </p>
                </li>
                <li>
                  <strong>Privacy.</strong> Customer assumes sole and full
                  responsibility for providing End-Users with any required
                  disclosure or explanation of the various features related to
                  Customer Content and any goods or services described therein,
                  as well as any rules, terms or conditions of use, including
                  matters related to data privacy and cybersecurity laws.
                  Customer is responsible for any consents and notices required
                  to permit:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      Customer's use and receipt of the Cloud Services; and
                    </li>
                    <li>
                      Company's accessing, storing, and processing of data
                      provided data to Company by Customer, including the
                      Customer Content, by Customer under the Agreements.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Cooperation.</strong> Customer will cooperate fully
                  with Company in connection with Company's delivery of the
                  Cloud Services necessary to perform under the Order. Customer
                  may be required to provide equipment and/or software
                  (additional to such stated in the Order) that may be necessary
                  for Customer to use the Cloud Services for Customer's intended
                  use of the Cloud Services.
                </li>
                <li>
                  <strong>Delays.</strong> Delays in Customer's performance of
                  its obligations under the Agreements may extend the time for
                  Company's performance to provide the Cloud Services.
                </li>
                <li>
                  <strong>Restrictions.</strong> Customer will not, and will not
                  allow End-Users to:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      copy (except for installation purposes), modify, or create
                      a derivative work of the Cloud Services;
                    </li>
                    <li>
                      reverse engineer, decompile, translate, disassemble, or
                      otherwise attempt to extract any or all of the source code
                      of, the Cloud Services (except to the extent such
                      restriction is expressly prohibited by applicable law);
                    </li>
                    <li>
                      sell, resell, sublicense, transfer, or distribute any or
                      all of the Cloud Services; or
                    </li>
                    <li>
                      access or use the Cloud Services:
                      <ol class="list-decimal pl-6 mt-2 mb-2">
                        <li>
                          for activities where the use or failure of the Cloud
                          Services would reasonably be expected to lead to
                          death, personal injury, or environmental or property
                          damage;
                        </li>
                        <li>in violation of the AUP;</li>
                        <li>
                          in a manner intended to avoid paying any amounts owed
                          by Customer;
                        </li>
                        <li>
                          to engage in cryptocurrency mining without Company's
                          prior written approval; or
                        </li>
                        <li>
                          in a manner that breaches, or causes the breach of
                          export and re-export control laws and regulations.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <strong>Documentation and Specifications.</strong> To use the
              Cloud Services and the extent available, at no additional charge
              to Customer, unless otherwise stated in an Order, Company shall
              provide or make available Customer with any documentation and
              specifications relating to the Cloud Services (collectively,
              "Documentation and Specifications"). If the Documentation and
              Specifications are revised or supplemented at any time, Company
              shall promptly deliver a copy of such revised or supplemental
              Documentation and Specifications to Customer or make such
              available, at no additional cost to Customer. Customer may, at any
              time, reproduce copies of all Documentation and Specifications and
              other materials provided by Company, distribute such copies to its
              personnel, and incorporate such copies into its own technical
              manuals, provided that such reproduction relates to Customer's and
              its personnel's use of the Cloud Services as permitted in the
              Agreements, and all copyright and trademark notices, if any, are
              reproduced thereon. To the maximum extent available, Company shall
              deliver the Documentation and Specifications in electronic form to
              Customer.
            </li>
            <li>
              <strong>
                Monitoring of Customer Content; License to Company; Suspension.
              </strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  Company does not review, pre-screen or monitor Customer
                  Content. However, Company reserves the right (but undertakes
                  no duty) to do so and decide whether any item of Customer
                  Content is appropriate and/or complies with the Agreements.
                </li>
                <li>
                  Customer hereby grants to Company a non-exclusive,
                  royalty-free, worldwide right and license during the Term (as
                  defined herein) to do the following to the extent necessary in
                  the performance of the Cloud Services:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      the right to reproduce, copy, use and distribute all and
                      any portion of the Customer Content to the extent needed
                      to perform the Cloud Services.
                    </li>
                    <li>
                      digitize, convert, install, upload, select, order,
                      arrange, compile, combine, synchronize, use, reproduce,
                      store, process, retrieve, transmit, distribute, publish,
                      publicly display, publicly perform and hyperlink the
                      Customer Content;
                    </li>
                    <li>
                      make archival or backup copies of the Customer Content
                      (although Company is not required to do so as Customer is
                      solely responsible for backing-up any Customer Content);
                    </li>
                    <li>
                      except for the rights expressly granted above, Company is
                      not acquiring any right, title or interest in or to the
                      Customer Content, all of which shall remain solely with
                      Customer; and
                    </li>
                    <li>
                      Company, in its sole discretion, reserves the right:
                      <ol class="list-decimal pl-6 mt-2 mb-2">
                        <li>
                          to deny, cancel, suspend, transfer or alter, modify,
                          correct, amend, change, program, or take any other
                          corrective action to protect the integrity and
                          stability of the Cloud Services (including altering,
                          modifying, correcting, amending, changing,
                          programming, or taking any other corrective action
                          regarding any malicious code, software or related
                          abusive activity, of the Customer Content and/or
                          website(s)); and/or
                        </li>
                        <li>
                          to comply with any applicable laws, government rules,
                          or requirements, requests of law enforcement, or to
                          avoid any liability, civil or criminal.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
                <li>
                  Customer further agrees that Company shall not be liable to
                  Customer for any loss or damages that may result from such
                  conduct.
                </li>
              </ol>
            </li>
            <li>
              <strong>Trademark and/or Copyright Claims.</strong> Company
              supports the protection of intellectual property. Company responds
              to notices of alleged copyright or trademark infringement claims
              and will terminate the Account of repeat infringers in appropriate
              circumstances as required to maintain safe harbor for online
              service providers under the applicable laws.
            </li>
            <li>
              <strong>Data and Security.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  Company may collect certain information about computing jobs
                  Customer utilizes via the Cloud Services, including but not
                  limited to CPU utilization, memory usage, IO performance, and
                  error and information messages.
                </li>
                <li>
                  Customer is solely responsible for monitoring the status of
                  all computing jobs. Company may throttle and/or terminate any
                  computing jobs that Company determines may degrade the
                  performance of the Cloud Services. Company is not responsible
                  for any data loss and/or data corruption that may occur as
                  part of Customer's computing jobs.
                </li>
              </ol>
            </li>
            <li>
              <strong>Customer's Representations and Warranties.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  Customer hereby represents and warrants to Company, and agrees
                  that during the term and any Term (as such terms are defined
                  herein) Customer will ensure that:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      Customer is and remains the legal owner or valid licensee
                      of the Customer Content and each element thereof, and
                      Customer has secured all necessary licenses, consents,
                      permissions, waivers and releases for the use of the
                      Customer Content and each element thereof, including
                      without limitation, all copyrights, trademarks, logos,
                      names and likenesses contained therein, without any
                      obligation by Company to pay any fees, residuals, guild
                      payments or other compensation of any kind to any person
                      or third party;
                    </li>
                    <li>
                      Customer's use, publication, and display of the Customer
                      Content will not infringe any copyright, patent,
                      trademark, trade secret or other proprietary or
                      intellectual property right of any person, or constitute a
                      defamation, invasion of privacy or violation of any right
                      of publicity or any other right of any person, including,
                      without limitation, any contractual, statutory or common
                      law right or any "moral right" or similar right however
                      denominated;
                    </li>
                    <li>
                      Customer will comply with all applicable laws, rules, and
                      regulations regarding the Customer Content and will use
                      the Customer Content only for lawful purposes; and
                    </li>
                    <li>
                      Customer has used and will continue to use its best
                      efforts to ensure that the Customer Content is and will at
                      all times remain free of all computer viruses, worms,
                      Trojan horses and/or other malicious code.
                    </li>
                  </ol>
                </li>
                <li>
                  Customer shall be solely responsible for the development,
                  operation and maintenance of Customer Content, electronic
                  commerce activities, all products and services offered by
                  Customer or appearing through Customer Content and for all
                  contents and materials appearing online or on Customer's
                  products or within any services, including, without
                  limitation:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      the accuracy and appropriateness of the Customer Content
                      and content and material appearing in its store or on its
                      products or services;
                    </li>
                    <li>
                      ensuring that the Customer Content and content and
                      materials appearing in its store or on its products do not
                      violate or infringe upon the rights of any person or third
                      party; and
                    </li>
                    <li>
                      ensuring that the Customer Content and the content and
                      materials appearing in Customer Content or electronic
                      commerce activities, products and services offered by
                      Customer are not defamatory or otherwise illegal. Customer
                      shall be solely responsible for accepting, processing and
                      filling customer orders and for handling customer
                      inquiries and/or complaints. Customer shall be solely
                      responsible for the payment or satisfaction of any and all
                      taxes associated with Customer Content, its website(s) and
                      electronic commerce activities.
                    </li>
                  </ol>
                </li>
                <li>
                  In addition to transactions entered into by Customer or on
                  Customer's behalf, Customer agrees to be bound by the terms of
                  the Agreements for transactions entered into on Customer's
                  behalf by anyone acting as Customer's agent, and transactions
                  entered into by anyone who uses Customer's Account, whether or
                  not the transactions were on Customer's behalf.
                </li>
              </ol>
            </li>
            <li>
              <strong>
                Cloud Services, Support,{' '}
                <a
                  href="/sla"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  SLAs
                </a>{' '}
                and Software.
              </strong>{' '}
              During the Term, Company shall:
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  Provide Customer with the Cloud Services set forth in the
                  Order.
                </li>
                <li>
                  Provide Customer with the support services set forth in the
                  Order.
                </li>
                <li>
                  Achieve the service levels and performance standards set forth
                  in the Order or{' '}
                  <a
                    href="/sla"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    SLAs
                  </a>
                  . Subject to the terms and conditions of the{' '}
                  <a
                    href="/sla"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    SLAs
                  </a>
                  , Company shall use commercially reasonable efforts to attempt
                  to provide the Cloud Services on twenty-four (24) hours a day,
                  seven (7) days a week basis, however, such up-time is not a
                  guarantee. Customer understands, acknowledges and agrees that
                  from time to time the Products and Subscriptions may be
                  inaccessible or inoperable for any reason including, but not
                  limited to, equipment malfunctions, periodic maintenance,
                  repairs or replacements that Company undertakes from time to
                  time or causes beyond Company's reasonable control or that are
                  not reasonably foreseeable including, but not limited to,
                  interruption or failure of telecommunication or digital
                  transmission links, hostile network attacks, network
                  congestion or other failures. Customer understands,
                  acknowledges and agrees that Company has no control over the
                  availability of the Products and Subscriptions on a
                  "guaranteed" continuous or uninterrupted basis and that
                  Company assumes no liability to Customer or any other party
                  with regard thereto.
                </li>
                <li>
                  During the Term, Company hereby grants to Customer a
                  non-exclusive, non-transferable, non-assignable,
                  non-sublicensable license to use any software products
                  provided related to an Order in accordance with the provisions
                  set out in the{' '}
                  <a
                    href="/terms-of-use"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    UToU
                  </a>{' '}
                  and the Order. Subject to mandatory applicable law(s),
                  Customer shall not
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      license, sublicense, sell, resell, transfer, assign,
                      distribute, or otherwise commercially exploit or make
                      available to any software provided under an Order in any
                      way;
                    </li>
                    <li>
                      modify or make derivative works based upon the software;
                    </li>
                    <li>
                      provide the software as a cloud service, service bureau,
                      or outsourcing service; or
                    </li>
                    <li>
                      reverse engineer or access the software in order to
                      <ol class="list-decimal pl-6 mt-2 mb-2">
                        <li>build a competitive product or service;</li>
                        <li>
                          build a product using similar ideas, features,
                          functions or graphics of the software;
                        </li>
                        <li>
                          copy any ideas, features, functions or graphics of the
                          software.
                        </li>
                      </ol>
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <strong>
                Intellectual Property Rights; Protection of Customer Data;
                Feedback.
              </strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Intellectual Property Rights.</strong> Except as
                  expressly stated in the Agreements, no Party is granted any
                  rights, implied or otherwise, to the other's content or any of
                  the other's intellectual property.
                </li>
                <li>
                  <strong>Protection of Customer Data.</strong> Company will
                  only access or use Customer Content to provide the Cloud
                  Services to Customer and will not use it for any other Company
                  products, services, or advertising. Company has implemented
                  and will maintain administrative, physical, and technical
                  safeguards to protect Customer Content, as further described
                  in the{' '}
                  <a
                    href="/data-processing"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    DPA
                  </a>
                  .
                </li>
                <li>
                  <strong>Customer Feedback.</strong> At its option, Customer
                  may provide feedback or suggestions about the Cloud Services
                  to Company ("Feedback"). If Customer provides Feedback, then
                  Company and its affiliates may use that Feedback without
                  restriction and without obligation to Customer.
                </li>
              </ol>
            </li>
            <li>
              <strong>Upgrades.</strong> Subject to the compatibility of any
              hardware or software installed by Customer (other than such
              delivered under the Order), Company may, as determined is
              Company's sole discretion, provide Customer with software version
              upgrades only, without additional charge to Customer, after
              commercial release ("Upgrades"). Upgrades do not include any
              hardware updates or upgrades. Upon delivery to Customer, each
              Upgrade will constitute an element of the applicable portion of
              the Cloud Services and will thereafter be subject to the terms and
              conditions contained in the CSA, including without limitation
              warranty, and indemnity terms.
            </li>
            <li>
              <strong>Technical Support Services</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>By Customer.</strong> Customer is responsible for
                  technical support to its End-Users.
                </li>
                <li>
                  <strong>By Company.</strong> Subject to payment of applicable
                  support fees as set forth in the Order, Company will provide
                  the support listed in the Order to Customer during the Term.
                </li>
              </ol>
            </li>
            <li>
              <strong>Confidential Information.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Confidential Information.</strong> "Confidential
                  Information" means the following that one party to this CSA
                  ("Discloser") discloses to the other ("Recipient"):
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>any document Discloser marks as "Confidential";</li>
                    <li>
                      any information Discloser orally designates as
                      "Confidential" at the time of disclosure, provided
                      Discloser confirms such designation in writing within
                      three business days;
                    </li>
                    <li>
                      the non-public features and functions of the Cloud
                      Services, for which Company is Discloser; and
                    </li>
                    <li>
                      any other non public, sensitive information Recipient
                      should reasonably consider a trade secret or otherwise
                      confidential.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Confidential Information Limitations.</strong>{' '}
                  Notwithstanding the foregoing, Confidential Information does
                  not include information that:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      is in Recipient's possession at the time of disclosure
                      (which has not previously been designated as Confidential
                      Information);
                    </li>
                    <li>
                      is independently developed by Recipient without use of or
                      reference to Confidential Information;
                    </li>
                    <li>
                      becomes known publicly, before or after disclosure, other
                      than as a result of Recipient's improper action or
                      inaction; or
                    </li>
                    <li>is approved for release in writing by Discloser.</li>
                  </ol>
                  <p class="mt-2">
                    Recipient is on notice that the Confidential Information may
                    include Discloser's valuable trade secrets.
                  </p>
                </li>
                <li>
                  <strong>Nondisclosure.</strong> Recipient shall not use
                  Confidential Information for any purpose other than to
                  facilitate the transactions contemplated by this CSA (the
                  "Purpose"). Recipient shall not:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      disclose Confidential Information to any employee or
                      contractor of Recipient unless such person needs access in
                      order to facilitate the Purpose and executes a
                      nondisclosure agreement with Recipient with terms no less
                      restrictive than those of this Section; and
                    </li>
                    <li>
                      disclose Confidential Information to any other third party
                      without Discloser's prior written consent. Without
                      limiting the generality of the foregoing, Recipient shall
                      protect Confidential Information with the same degree of
                      care it uses to protect its own confidential information
                      of similar nature and importance, but with no less than
                      reasonable care. Recipient shall promptly notify Discloser
                      of any misuse or misappropriation of Confidential
                      Information that comes to Recipient's attention.
                      Notwithstanding the foregoing, Recipient may disclose
                      Confidential Information as required by applicable law or
                      by proper legal or governmental authority. Recipient shall
                      give Discloser prompt notice of any such legal or
                      governmental demand and reasonably cooperate with
                      Discloser in any effort to seek a protective order or
                      otherwise to contest such required disclosure, at
                      Discloser's expense.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>Required Disclosure.</strong> Notwithstanding any
                  provision to the contrary in this CSA, the Recipient or its
                  affiliates may also disclose Confidential Information to the
                  extent required by applicable request made under law,
                  governmental regulation, court order, subpoena, warrant, or
                  other valid legal authority, legal procedure, or similar
                  process; provided that the Recipient or its affiliate uses
                  commercially reasonable efforts to:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      promptly notify the other party before any such disclosure
                      of its Confidential Information; and
                    </li>
                    <li>
                      comply with the other party's reasonable requests
                      regarding its efforts to oppose the disclosure.
                    </li>
                  </ol>
                  <p class="mt-2">
                    Notwithstanding the foregoing, subsections above will not
                    apply if the Recipient determines that complying with such
                    subsections could result in a violation of the applicable
                    legal request; obstruct a governmental investigation; or
                    lead to death or serious physical harm to an individual.
                  </p>
                </li>
                <li>
                  <strong>Injunction.</strong> Recipient agrees that breach of
                  this Section would cause Discloser irreparable injury, for
                  which monetary damages would not provide adequate
                  compensation, and that in addition to any other remedy,
                  Discloser will be entitled to injunctive relief against such
                  breach or threatened breach, without proving actual damage or
                  posting a bond or other security.
                </li>
                <li>
                  <strong>
                    Termination and Return of Confidential Information.
                  </strong>{' '}
                  With respect to each item of Confidential Information, the
                  obligations of Section 15.3 above (Nondisclosure) will
                  terminate three (3) years after the date of termination of
                  this CSA; provided that such obligations related to
                  Confidential Information constituting Discloser's trade
                  secrets shall continue so long as such information remains
                  subject to trade secret protection pursuant to applicable law.
                  Upon request of Discloser or termination of this CSA,
                  Recipient shall return all copies of Confidential Information
                  to Discloser or certify, in writing, the destruction thereof.
                </li>
                <li>
                  <strong>Retention of Rights.</strong> This CSA does not
                  transfer ownership of Confidential Information or grant a
                  license thereto. Discloser will retain all right, title, and
                  interest in and to all Confidential Information.
                </li>
              </ol>
            </li>
            <li>
              <strong>Representations And Warranties.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>From Company.</strong>
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      <strong>Function.</strong> Company represents and warrants
                      that, during the Term, the Cloud Service will perform
                      materially as described in the Documentation and
                      Specifications. HOWEVER, COMPANY DOES NOT WARRANT THAT THE
                      CLOUD SERVICES WILL BE PERFORMED ERROR-FREE OR
                      UNINTERRUPTED, THAT COMPANY WILL CORRECT ALL CLOUD SERVICE
                      ERRORS, OR THAT THE CLOUD SERVICES WILL MEET CUSTOMER'S
                      REQUIREMENTS OR EXPECTATIONS. COMPANY IS NOT RESPONSIBLE
                      FOR ANY ISSUES RELATED TO THE PERFORMANCE, OPERATION, OR
                      SECURITY OF THE CLOUD SERVICES THAT ARISE FROM CUSTOMER
                      CONTENT OR THIRD-PARTY CONTENT, APPLICATIONS OR SERVICES
                      PROVIDED BY THIRD PARTIES. In the event of breach of the
                      warranty in the preceding sentences, Company shall (to the
                      extent possible or practical):
                      <ol class="list-decimal pl-6 mt-2 mb-2">
                        <li>repair the Cloud Services;</li>
                        <li>
                          replace the Cloud Services with hardware software of
                          substantially similar functionality; or
                        </li>
                        <li>
                          if such attempts do not succeed after sixty (60) days,
                          this CSA shall be declared null and void. The
                          preceding sentence, in conjunction with Customer's
                          right to terminate this CSA for breach where
                          applicable, states Customer's sole remedy and
                          Company's entire liability for breach of the warranty
                          in this Subsection 16.1.
                        </li>
                      </ol>
                    </li>
                    <li>
                      <strong>Intellectual Property.</strong> To the extent
                      applicable under law, Company represents and warrants that
                      it is the owner of the Cloud Services and of each and
                      every component thereof, or the recipient of a valid
                      license thereto (except for any publicly accessible (i.e.,
                      open source) aspects of the Cloud Services), that it has
                      and will maintain the full power and authority to grant
                      the intellectual property rights to the Cloud Services set
                      forth in this CSA, without the further consent of any
                      third party, and that the Cloud Services do not infringe
                      on the rights of any third party. In the event the of
                      breach of the warranty in this Subsection 16.1.2 (to the
                      extent possible or practical), Company shall, at its own
                      expense, replace or modify the Cloud Services to make it
                      non-infringing, provided such modification or replacement
                      does not materially degrade any functionality set forth in
                      the Documentation and Specifications; provided if the
                      foregoing is not possible or practical, Company may
                      terminate this CSA. In conjunction with Section 16.1 (From
                      Company), the preceding sentence states Company's sole
                      obligation and liability, and Customer's sole remedy, for
                      potential or actual intellectual property infringement by
                      the Cloud Services.
                    </li>
                  </ol>
                </li>
                <li>
                  <strong>From Both Parties.</strong> Each Party represents and
                  warrants to one another that each has the full right and
                  authority to enter into, execute, and perform its obligations
                  under this CSA and that no pending or threatened claim or
                  litigation known to it would have a material adverse impact on
                  its ability to perform as required by this CSA.
                </li>
                <li>
                  <strong>Warranty Disclaimers.</strong> Except for the express
                  warranties in Sections 16.1 and 16.2 above, COMPANY DOES NOT
                  MAKE AND EXPRESSLY DISCLAIMS TO THE FULLEST EXTENT PERMITTED
                  BY APPLICABLE LAW ANY WARRANTIES OF ANY KIND, WHETHER EXPRESS,
                  IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING WARRANTIES OF
                  MERCHANTABILITY, FITNESS FOR A PARTICULAR USE, TITLE,
                  NONINFRINGEMENT, OR ERROR-FREE OR UNINTERRUPTED USE OF THE
                  CLOUD SERVICES AND ANY REPRESENTATIONS ABOUT CONTENT OR
                  INFORMATION ACCESSIBLE THROUGH THE CLOUD SERVICES.
                </li>
                <li>
                  Company does not warrant that the Cloud Services will perform
                  without error or that it will run without immaterial
                  interruption. Company provides no warranty regarding, and will
                  have no responsibility for, any claim arising out of:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      a modification of the Cloud Services made by anyone other
                      than Company, unless Company approves such modification in
                      writing; or
                    </li>
                    <li>
                      use of the Cloud Services in combination with any
                      operating system not authorized in the Specifications or
                      Documentation or with hardware or software specifically
                      forbidden by the Documentation and Specifications.
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <strong>Indemnification.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>From Company.</strong> Company shall defend and
                  indemnify Customer and Customer's Associates (as defined below
                  in Section 17.3) against any "Indemnified IP Claim," meaning
                  any third-party claim, suit, or proceeding arising out of,
                  related to, or alleging direct infringement of any patent,
                  copyright, trade secret, or other intellectual property right
                  by the Cloud Services. Company's obligations set forth in this
                  Section 17.1 do not apply to the extent that an Indemnified IP
                  Claim arises out of
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>Customer's breach of this CSA;</li>
                    <li>
                      revisions to the Cloud Services made without Company's
                      written consent;
                    </li>
                    <li>
                      Customer's failure to incorporate Upgrades that would have
                      avoided the alleged infringement, provided Company offered
                      such Upgrades without charges not otherwise required
                      pursuant to this CSA;
                    </li>
                    <li>
                      use of the Cloud Services in combination with hardware or
                      software other than as specified by Company.
                    </li>
                  </ol>
                  <p class="mt-2">
                    In the event of an Indemnified IP Claim, Company may
                    exercise any of the remedies set forth in Subsection 17.1
                    above, including without limitation its right therein to
                    terminate this CSA.
                  </p>
                </li>
                <li>
                  <strong>From Customer.</strong> Customer shall defend and
                  indemnify Company and Company's Associates (as defined below)
                  against any "Indemnified Claim," meaning any third-party
                  claim, suit, or proceeding:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      by any sub-customer or End-User, as well as any such
                      entity's employee, contractor; or
                    </li>
                    <li>Company's material breach of this CSA.</li>
                  </ol>
                  <p class="mt-2">
                    Notwithstanding the foregoing, no such claim, suit, or
                    proceeding shall be considered an Indemnified Claim pursuant
                    to this Section 17.2 to the extent that it arises out of,
                    relates to, or alleges: intellectual property infringement
                    by the Cloud Services; or an injury caused by the Cloud
                    Services's failure to conform to its Documentation or
                    Specifications. For the avoidance of doubt, Indemnified
                    Claims do not include claims related to injuries to the
                    extent caused by the Subscription's failure to perform as
                    represented by Company but not by the Documentation or
                    Specifications.
                  </p>
                </li>
                <li>
                  <strong>Litigation and Additional Terms.</strong> The
                  obligations of the indemnifying party ("Indemnitor") pursuant
                  to Sections 17.1 or 17.2 above:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      include retention and payment of attorney's and expert
                      fees and payment of court costs, as well as settlement at
                      Indemnitor's expense and payment of judgments; and
                    </li>
                    <li>
                      will be excused to the extent that the other contracting
                      party's ("Indemnified Party's") or any of such Indemnified
                      Party's Associates' failure to provide prompt notice of
                      the Indemnified Claim or reasonably to cooperate
                      materially prejudices the defense. Indemnitor will control
                      the defense of any Indemnified Claim, including appeals,
                      negotiations, and any settlement or compromise thereof;
                      provided Indemnified Party will have the right, not to be
                      exercised unreasonably, to reject any settlement or
                      compromise that requires that it admit wrongdoing or
                      liability or subjects it to any ongoing affirmative
                      obligations. As used in this Section 17, a Party's
                      "Associates" are its officers, directors, managers,
                      shareholders, members, parents, subsidiaries, agents,
                      successors, and assigns. Customer's "Associates" also
                      include sub-customers and end-customers, provided each
                      such sub-customer or end-customer was rightfully licensed
                      pursuant to this CSA.
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <strong>Limitation of Liability.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Limitation on Indirect Liability.</strong> To the
                  extent permitted by applicable law and subject to Section 15
                  (Confidential Information) and Section 18.3 (Clarifications
                  and Disclaimers), IN NO EVENT SHALL EITHER PARTY BE LIABLE TO
                  ANYONE FOR ANY INDIRECT, PUNITIVE, SPECIAL, EXEMPLARY,
                  INCIDENTAL, CONSEQUENTIAL, OR OTHER DAMAGES OF ANY TYPE OR
                  KIND (INCLUDING LOSS OF DATA, REVENUE, PROFITS, USE, OR OTHER
                  ECONOMIC ADVANTAGE) ARISING OUT OF, OR IN ANY WAY CONNECTED
                  WITH THE CLOUD SERVICES (INCLUDING SOFTWARE PRODUCT OR SUPPORT
                  SERVICES), INCLUDING BUT NOT LIMITED TO THE USE OR INABILITY
                  TO USE THE CLOUD SERVICES OR SUPPORT SERVICES, OR FOR ANY
                  CONTENT OR DATA OBTAINED FROM OR THROUGH THE CLOUD SERVICES OR
                  SUPPORT SERVICES, ANY INTERRUPTION, INACCURACY, ERROR, OR
                  OMISSION, REGARDLESS OF CAUSE IN THE CONTENT, EVEN IF THE
                  PARTY FROM WHICH DAMAGES ARE BEING SOUGHT HAS BEEN PREVIOUSLY
                  ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
                </li>
                <li>
                  <strong>Dollar Cap.</strong> TO THE FURTHEST EXTENT ALLOWED BY
                  APPLICABLE LAW, IN NO EVENT SHALL EITHER PARTY'S CUMULATIVE
                  LIABILITY ARISING OUT OF OR RELATED TO THIS CSA WILL NOT
                  EXCEED THE AMOUNT OF FEES ACTUALLY PAID TO COMPANY BY CUSTOMER
                  WITHIN THE PRECEDING SIX (6) MONTHS THE EVENT GIVING RISE TO
                  SUCH CLAIM, EXCEPT COMPANY'S TOTAL AGGREGATE LIABILITY FOR
                  DAMAGES ARISING OUT OF OR RELATED TO CLOUD SERVICES IS LIMITED
                  TO $1,000 USD.
                </li>
                <li>
                  <strong>Clarifications and Disclaimers.</strong> Nothing in
                  this CSA excludes or limits either Party's liability for:
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>fraud or fraudulent misrepresentation;</li>
                    <li>obligations under Section 17 (Indemnification);</li>
                    <li>
                      its infringement of the other Party's intellectual
                      property rights;
                    </li>
                    <li>its payment obligations under the CSA; or</li>
                    <li>
                      matters for which liability cannot be excluded or limited
                      under applicable law.
                    </li>
                  </ol>
                  <p class="mt-2">
                    If applicable law(s) limits the application of the
                    provisions of this Section 18.3 the Parties' liability will
                    be limited to the maximum extent permissible by law. For the
                    avoidance of doubt, each Party's liability limits, and other
                    rights set forth in this Section apply likewise to its
                    Associates, affiliates, licensors, suppliers, advertisers,
                    agents, sponsors, directors, officers, employees,
                    consultants, and other representatives.
                  </p>
                </li>
              </ol>
            </li>
            <li>
              <strong>
                Dispute Resolution and Arbitration; Class Action Waiver.
              </strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Applicable Law.</strong> This CSA and any dispute that
                  may arise between Customer and Company shall be resolved by
                  binding arbitration and governed by and construed in
                  accordance with Nigerian Arbitration law, as may be amended.
                </li>
                <li>
                  <strong>Dispute Resolution.</strong> Before either Party may
                  seek arbitration as provided below, the complaining party must
                  first send to the other party a written notice of dispute
                  ("Notice") describing the nature and basis of the claim or
                  dispute, and the requested relief. After the Notice is
                  received, the Parties may attempt to resolve the claim or
                  dispute informally. If the Parties do not resolve the claim or
                  dispute within sixty (60) calendar days after the Notice is
                  received, either Party may begin an arbitration proceeding as
                  provided below.
                </li>
                <li>
                  <strong>Arbitration.</strong> Customer agrees to resolve the
                  dispute in a cost-effective manner through binding arbitration
                  (including non-appearance-based arbitration). In such event,
                  Customer hereby agrees the arbitration procedure shall be
                  initiated through the applicable rules under the Nigerian
                  Arbitration law.
                </li>
                <li>
                  <strong>Waiver of Class or Consolidated Actions.</strong> ALL
                  CLAIMS AND DISPUTES WHICH MAY BE MADE WITHIN THE SCOPE OF THIS
                  CSA MUST BE ARBITRATED OR LITIGATED ON AN INDIVIDUAL BASIS AND
                  NOT ON A CLASS BASIS, AND CLAIMS OF MORE THAN ONE CUSTOMER
                  CANNOT BE ARBITRATED OR LITIGATED JOINTLY OR CONSOLIDATED WITH
                  THOSE OF ANY OTHER CUSTOMER. CUSTOMER UNDERSTANDS AND AGREES
                  THAT, BY ENTERING INTO THE AGREEMENTS, CUSTOMER WAIVES
                  PARTICIPATING IN A CLASS ACTION.
                </li>
              </ol>
            </li>
            <li>
              <strong>Suspension.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>
                    <a
                      href="/acceptable-use-policy"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-800 no-underline"
                    >
                      AUP
                    </a>{' '}
                    Violations.
                  </strong>{' '}
                  If Company becomes aware that Customer's or any End-User's use
                  of the Cloud Services violates the{' '}
                  <a
                    href="/acceptable-use-policy"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    AUP
                  </a>
                  , Company will notify Customer and request that Customer
                  correct the violation. If Customer fails to correct the
                  violation within 24 hours of Company's request, then Company
                  may disable or limit access to or use of the Cloud Services or
                  components of the Cloud Services until the violation is
                  corrected.
                </li>
                <li>
                  <strong>Other Suspension.</strong> Notwithstanding Section
                  20.1 (
                  <a
                    href="/acceptable-use-policy"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    AUP
                  </a>{' '}
                  Violations), Company may immediately suspend all or part of
                  Customer's use of the Cloud Services if
                  <ol class="list-decimal pl-6 mt-2 mb-2">
                    <li>
                      Company reasonably believes Customer's or any End-User's
                      use of the Cloud Services could adversely impact the Cloud
                      Services, other Company customers' or its end-users use of
                      the Cloud Services or the Company network or servers used
                      to provide the Cloud Services;
                    </li>
                    <li>
                      there is suspected unauthorized third-party access to the
                      Cloud Services; or
                    </li>
                    <li>
                      Company reasonably believes that immediate suspension is
                      required to comply with any applicable law.
                    </li>
                  </ol>
                  <p class="mt-2">
                    Company will lift any such suspension when the circumstances
                    giving rise to the suspension have been resolved.
                  </p>
                </li>
                <li>
                  Specific paid Cloud Services may also be suspended if Customer
                  fails to make payment within a specified period. Such periods
                  will be communicated to Customers at the commencement of the
                  use of prospective cloud services or at any time during the
                  period when the services are used by Customers.
                </li>
              </ol>
            </li>
            <li>
              <strong>General Terms.</strong>
              <ol class="list-decimal pl-6 mt-2 mb-2">
                <li>
                  <strong>Headings.</strong> Headings and captions used in this
                  CSA are for reference purposes only and will not have any
                  effect on the interpretation of the Agreements.
                </li>
                <li>
                  <strong>Electronic Communications.</strong> The Parties may
                  use electronic mail ("e-mail") to satisfy written approval and
                  consent requirements under the Agreements.
                </li>
                <li>
                  <strong>Independent Contractors.</strong> The Parties are
                  independent contractors and will represent themselves in all
                  regards. Neither Party is the agent of the other, and neither
                  may make commitments on the other's behalf.
                </li>
                <li>
                  <strong>No Third-Party Beneficiaries.</strong> Except as
                  otherwise specifically provided herein, this CSA shall inure
                  to the benefit of the Parties only, and this CSA does not
                  confer any benefits on any third party unless it expressly
                  states that it does.
                </li>
                <li>
                  <strong>Notices.</strong> Notices pursuant to this CSA shall
                  be sent to Customer at the email in the Account and notices to
                  Company must be sent to Company's email address. By submitting
                  a request Customer agrees to the terms of the{' '}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    Privacy Policy
                  </a>{' '}
                  regarding Customer's personally identifiable information.
                </li>
                <li>
                  E-Mail notice will be treated as received when the email is
                  sent. Customer is responsible for keeping the email in the
                  Account current throughout the Term. To the extent legally
                  required, notices pursuant to this CSA may be sent to
                  addresses listed in the Order, or to such others as either
                  Party may provide in writing; in such, event, such notices
                  will be deemed received at such addresses upon the earlier of
                  actual receipt or delivery in person, by nationally recognized
                  overnight courier, by email with written confirmation of
                  receipt, or by certified mail return receipt requested.
                </li>
                <li>
                  <strong>Force Majeure.</strong> Neither Party will be liable
                  for failure or delay in performance to the extent caused by
                  circumstances beyond its reasonable control, including,
                  without limitation to, acts of war, terrorism, hurricanes,
                  earthquakes, other acts of God or of nature, strikes or other
                  labor disputes, riots or other acts of civil disorder,
                  embargoes, epidemics, pandemics, or other causes beyond the
                  performing party's reasonable control.
                </li>
                <li>
                  <strong>Assignment and Successors.</strong> This CSA shall
                  extend to and be binding upon the Parties hereto and their
                  respective successors and assigns. Customer may not assign
                  this CSA. Company may assign the Order and this CSA in the
                  event of a transfer of Company to an affiliated entity; if
                  Company transfers substantially all of the assets related to
                  its rights and obligations hereunder to a third party; or if
                  Company is acquired, reorganizes or merges, provided that, in
                  any such event, to the extent legally able, Company shall
                  provide Customer with at least sixty (60) days prior written
                  notice.
                </li>
                <li>
                  <strong>Change of Control.</strong> If a Party experiences a
                  change of control (i.e., greater than 50 percent of the voting
                  rights or equity interests of a party) other than as part of
                  an internal restructuring or reorganization (for example,
                  through a stock purchase or sale, merger, or other form of
                  corporate transaction), that party will give written notice to
                  the other party within thirty (30) days after the change of
                  control.
                </li>
                <li>
                  <strong>Subcontracting.</strong> Company may subcontract
                  obligations under the Agreements but will remain liable to
                  Customer for any subcontracted obligations.
                </li>
                <li>
                  <strong>No Agency.</strong> The Agreements do not create any
                  agency, partnership, or joint venture between the Parties.
                </li>
                <li>
                  <strong>Severability.</strong> To the extent permitted by
                  applicable law, the Parties hereby waive any provision of law
                  that would render any clause of this CSA invalid or otherwise
                  unenforceable in any respect. In the event that a provision of
                  this CSA is held to be invalid or otherwise unenforceable,
                  such provision will be interpreted to fulfill its intended
                  purpose to the maximum extent permitted by applicable law, and
                  the remaining provisions of this CSA will continue in full
                  force and effect.
                </li>
                <li>
                  <strong>No Waiver.</strong> Neither Party will be deemed to
                  have waived any of its rights under this CSA by lapse of time
                  or by any statement or representation other than by an
                  authorized representative in an explicit written waiver. No
                  waiver of a breach of this CSA will constitute a waiver of any
                  other breach of this CSA.
                </li>
                <li>
                  <strong>Bankruptcy Rights.</strong> Each Party may terminate
                  this CSA, without prejudice to any other remedy it may have
                  immediately without further obligation, in the event the other
                  Party makes an assignment for the benefit of its creditors,
                  files under any voluntary bankruptcy or insolvency law, under
                  the reorganization or arrangement provisions of the relevant
                  laws.
                </li>
                <li>
                  <strong>Choice of Law and Jurisdiction:</strong> This CSA will
                  be governed solely by the laws of the Federal Republic of
                  Nigeria, without reference to any conflicts of law principle
                  that would apply the substantive laws of another jurisdiction
                  to the Parties' rights or duties; the 1980 United Nations
                  Convention on Contracts for the International Sale of Goods;
                  or other international laws. The Parties consent to the
                  jurisdiction of the federal and state courts of Nigeria.
                </li>
                <li>
                  <strong>Equitable Relief.</strong> Nothing in the Agreements
                  will limit either Party's ability to seek equitable relief.
                </li>
                <li>
                  <strong>Construction.</strong> The Parties agree that the
                  terms of this CSA result from negotiations between them. this
                  CSA will not be construed in favor of or against either Party
                  by reason of authorship.
                </li>
                <li>
                  <strong>Technology Export.</strong> Customer shall not: (a)
                  permit any third party to access or use the Cloud Services in
                  violation of any Nigerian law or regulation; or (b) export the
                  Cloud Services or otherwise remove it from Nigeria except in
                  compliance with all applicable laws and regulations. Without
                  limiting the generality of the foregoing, Customer shall not
                  permit any third party to access or use the Cloud Services in,
                  or export it to, a country subject to a United States embargo.
                </li>
                <li>
                  <strong>Attorney's Fees and Costs.</strong> Claims for
                  attorney's fees and other costs (including expert fees) are
                  recoverable by the prevailing party in any legal action
                  (including arbitration) associated with the enforcement of
                  this CSA, or the collection of any amounts due under this CSA.
                </li>
                <li>
                  <strong>Entire Agreement.</strong> The Order and this CSA sets
                  forth the entire agreement of the Parties and supersedes all
                  prior or contemporaneous writings, negotiations, and
                  discussions with respect to its subject matter. Neither Party
                  has relied upon any such prior or contemporaneous
                  communications.
                </li>
                <li>
                  <strong>Amendments.</strong> Except as stated in Section 3.3.2
                  (Modifications: To the Agreement) or (c) Section 3.3.3
                  (Modifications: To the{' '}
                  <a
                    href="/data-processing"
                    target="_blank"
                    className="text-blue-600 hover:text-blue-800 no-underline"
                  >
                    Data Processing Addendum
                  </a>
                  ), any amendment must be in writing, signed by both Parties,
                  and expressly state that it is amending this CSA.
                </li>
                <li>
                  <strong>Entire Agreement.</strong> The Agreements set out all
                  terms agreed between the Parties and supersedes all other
                  agreements between the Parties relating to its subject matter.
                  In entering into the Agreements, neither Party has relied on,
                  and neither Party will have any right or remedy based on, any
                  statement, representation, or warranty (whether made
                  negligently or innocently), except those expressly stated in
                  the Agreements.
                </li>
                <li>
                  <strong>Survival.</strong> The following Sections will survive
                  expiration or termination of this CSA: Section 2.4 (Effect of
                  Termination), Section 4 (Payment Terms), Section 12
                  (Intellectual Property Rights; Protection of Customer Data;
                  Feedback), Section 15 (Confidential Information), Section 16.2
                  (Warranty Disclaimers), Section 17 (Indemnification), Section
                  18 (Limitation of Liability), Section 18.3 (Clarifications and
                  Disclaimers), Section 21 (General Terms).
                </li>
                <li>
                  <strong>Conflicting Languages.</strong> If the Agreements are
                  translated into any language other than English, and there is
                  a discrepancy between the English text and the translated
                  text, the English text will govern unless expressly stated
                  otherwise in the translation.
                </li>
              </ol>
            </li>
          </li>
        </ol>
      </section>
    </div>
  );
};

export default CloudServicesAgreement;
